import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";

import { Upload } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { Modal } from "@/shared/ui/Modal";
import {
  useClansProfiles,
  useCreateClanProfile,
  useEditClanProfile,
  useMyClanId,
} from "@/entities/clans";
import {
  createClanProfileSchema,
  CreateClanProfileSchema,
} from "./model/schema";
import { Textarea } from "@/shared/ui/textarea";

export const CreateClanProfileModal = () => {
  const [open, setOpen] = useState(false);
  const clanId = useMyClanId();
  const { data: clanProfiles } = useClansProfiles(
    {
      currentPage: 1,
      pageSize: 1,
      clanIds: [clanId ?? ""],
    },
    { enabled: !!clanId },
  );
  const { mutateAsync: createClanProfile } = useCreateClanProfile();
  const { mutateAsync: editClanProfile } = useEditClanProfile();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<CreateClanProfileSchema>({
    defaultValues: {
      description: "",
      image: undefined,
    },
    resolver: zodResolver(createClanProfileSchema),
  });

  useEffect(() => {
    if (clanProfiles?.data[0]) {
      setValue("description", clanProfiles.data[0].presentation.description);
    }
  }, [clanProfiles, setValue]);

  const onSubmit = async (data: CreateClanProfileSchema) => {
    if (clanProfiles?.total) {
      await editClanProfile({ clanId: clanId ?? "", ...data });
    } else {
      await createClanProfile({ clanId: clanId ?? "", ...data });
    }
    setOpen(false);
  };

  return (
    <Modal open={open} onOpenChange={(open) => setOpen(open)}>
      <Modal.Trigger variant="outline" asChild>
        <div>
          <Label className={`text-sm font-normal md:text-[12px]`}>Анкета</Label>
          <Button variant="outline" className="w-full text-textColor">
            {clanProfiles?.total ? "Редактировать анкету" : "Заполнить анкету"}
          </Button>
        </div>
      </Modal.Trigger>
      <Modal.Content>
        <Modal.Title>
          {clanProfiles?.total
            ? "Редактировать анкету клана"
            : "Создать анкету клана"}
        </Modal.Title>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="text">Сопроводительный текст</Label>
            <Textarea
              className="min-h-32"
              id="text"
              placeholder="..."
              {...register("description")}
            />
            {errors.description && (
              <p className="text-sm text-red-500">
                {errors.description.message}
              </p>
            )}
          </div>

          <Controller
            control={control}
            name="image"
            render={({ field: { onChange, value } }) => (
              <div className="flex items-center gap-2 rounded-lg bg-gray-100 p-3 dark:bg-gray-800">
                <label className="flex w-full cursor-pointer items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Upload className="min-w-6 text-[#2990FF]" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {value?.name ||
                        "Изображение для анкеты. (Разрешение изображения не должно превышать 512×320px, изображение автоматически конвертируется в JPEG)"}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept="image/jpeg, image/png"
                    className="hidden"
                    onChange={(e) => onChange(e.target.files?.[0])}
                  />
                  <span className="text-sm text-[#2990FF]">Загрузить</span>
                </label>
              </div>
            )}
          />
          {errors.image && (
            <p className="text-sm text-red-500">
              {errors.image.message as string}
            </p>
          )}

          <div className="flex flex-col gap-2">
            <Button type="submit">Отправить анкету клана</Button>
            <Modal.Close />
          </div>
        </form>
      </Modal.Content>
    </Modal>
  );
};
